import {
  FluidAnchorPosition,
  FluidKeyedObject,
  FluidSize,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { CloseButton } from '../../common/fluid-common-button.components';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { ESCAPE, EventUtils } from '../../utils/events/event-utils';
import { themeVar } from '../../utils/themes/theme.variables';
import { Utils } from '../../utils/utils';

/**
 * @displayName Drawer
 *
 * @contextData While similar to FLUID Sidebar in appearance, FLUID Drawer is closer to a Modal or Popover in purpose. FLUID Drawer's primary use case is for contextual information that is related to content within the page (especially when a Popover is too small, or a Modal is too intrusive). The Drawer component can be used for ancillary content, reference material, settings controls, etc., but should NOT be used primarily for navigation (except in very rare circumstances such as contextual links to relevant in-page material).
 *
 * @slot - Content displayed within the Drawer
 */

@Component({
  tag: 'fluid-drawer',
  styleUrl: 'fluid-drawer.less',
  shadow: true,
})
export class FluidDrawer implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to the Drawer container.
   */
  private _containerRef: HTMLElement;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidDrawerElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: {
    dismissButton?: {
      title?: string;
    };
  };
  private _translateId: string;

  private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * When true, indicates that the Drawer is actively being animated
   * open or closed. Used to remove "transform" styles on main container
   * when fully open (prevents issues with PopperJS-enabled components).
   */
  @State() _transitioning = false;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Whether the Drawer is open or closed. When true, Drawer will be open.
   * When false, Drawer will be closed.
   */
  @Prop({ mutable: true }) visible = false;

  @Watch('visible')
  onVisibleChange() {
    this._transitioning = true;
    this.toggleScrollDisabled();
    this.visible &&
      Utils.nextTick().then(() => Utils.setFocus(this._containerRef));
    this.drawerToggled.emit(this.visible);
  }

  /**
   * Determines which side of the page that Drawer will be positioned.
   */
  @Prop({ mutable: true }) anchor: FluidAnchorPosition =
    FluidAnchorPosition.LEFT;

  /**
   * Determines the Drawer's width (if anchor is left/right) or height
   * (if anchor is top/bottom). Accepts FluidSize for t-shirt sizing, or
   * a number that equates to a percentage of the viewport width/height.
   * For example, if the anchor Prop is set to "left" & a value of 60
   * is passed, the Drawer will be 60% of the viewport width.
   */
  @Prop({ mutable: true }) size: FluidSize | number = FluidSize.SMALL;

  /**
   * Determines the size of the Drawer container's internal padding.
   * Accepts all FluidSize values except for "full".
   */
  @Prop({ mutable: true }) padding: Exclude<FluidSize, FluidSize.FULL> =
    FluidSize.SMALL;

  /**
   * If true, the Drawer will close on a click anywhere outside of the
   * main Drawer container. If false, the "dismiss" button must be clicked
   * to close the Drawer.
   */
  @Prop({ mutable: true }) outsideClickClose = true;

  /**
   * When true, removes drawers "close" button. NOTE: This should be used
   * with caution - if you haven't set up a way to close programmatically
   * with toggleDrawer(visible: boolean) on the instance OR enabled "outsideClickClose",
   * you won't be able to close the drawer!
   */
  @Prop({ mutable: true }) hideCloseIcon = false;

  /**
   * If true, background (page body) scrolling will be disabled when
   * Drawer is open. If false, page can be scrolled in the background
   * while Drawer is open.
   */
  @Prop({ mutable: true }) disableScroll = true;

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when Drawer is opened or closed. Emits "true" when
   * Drawer is opened & "false" when closed.
   */
  @Event() drawerToggled: EventEmitter<boolean>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - Set component z-index relative to global base value
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)();
    Utils.setZIndex(this.host);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: toggleScrollDisabled - disable background scrolling if Drawer is open
   */
  componentWillLoad() {
    this.toggleScrollDisabled();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action: enableBodyScroll - re-enable scrolling if component is removed from DOM
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
    Utils.enableBodyScroll();
    delete this._containerRef;
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Closes Modal when "escape" key pressed UNLESS "outsideClickClose" is
   * disabled AND "close" button is disabled/hidden.
   */
  @Listen('keydown')
  onKeyDown(event: KeyboardEvent) {
    switch (true) {
      case !EventUtils.isKey(event, ESCAPE):
      case !this.outsideClickClose && this.hideCloseIcon:
        return;
      default:
        this.visible = false;
    }
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Allows the drawer to be opened programmatically without maintaining
   * visibility state.
   */
  @Method()
  async openDrawer() {
    this.visible = true;
  }

  /**
   * Toggles Drawer open (true) or closed (false/null).
   */
  @Method()
  async toggleDrawer(visible = !this.visible) {
    this.visible = visible;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * If disableScroll Prop is true & Drawer is open, disables background
   * page scrolling.
   */
  toggleScrollDisabled(): void {
    if (this.visible && this.disableScroll) {
      Utils.disableBodyScroll();
    } else {
      Utils.enableBodyScroll();
    }
  }

  /**
   * If outsideClickClose Prop is true, toggles Drawer open/closed.
   * Called when page overlay is clicked.
   */
  handleOutsideClick(): void {
    this.outsideClickClose && this.toggleDrawer().then();
  }

  /**
   * Updates "_transitioning" State when Drawer is fully open/closed
   * (i.e. CSS "transition" is complete).
   * @param event - TransitionEvent emitted from ".drawer-container" element
   */
  handleTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== 'transform') return;
    this._transitioning = false;
  }

  /**
   * Returns Drawer container's inline width/height styles (if size Prop
   * is a number), or empty object if "size" Prop is FluidSize.
   */
  getSizeStyles(): FluidKeyedObject<string> {
    const verticalDrawer =
      this.anchor === FluidAnchorPosition.TOP ||
      this.anchor === FluidAnchorPosition.BOTTOM;

    switch (true) {
      case !Utils.isNumber(this.size):
        return {};
      case verticalDrawer:
        return { height: `${this.size}vh` };
      default:
        return { width: `${this.size}vw` };
    }
  }

  // ================================================================ //

  render() {
    const isFluidSize = Utils.isFluidEnum<FluidSize>(this.size, FluidSize);
    const iconScale = themeVar<number>(
      this.theme,
      'icons.drawer.dismiss_scale'
    );

    return (
      <div
        style={{ 'z-index': `${this.zIndex}` }}
        class={{
          [`fluid-drawer ${this.theme}`]: true,
          'hide-root': !this.visible,
        }}>
        <div
          role='presentation'
          onClick={() => this.handleOutsideClick()}
          class={{
            'page-overlay': true,
            'overlay-visible': this.visible,
            'overlay-hidden': !this.visible,
          }}
        />
        <div
          ref={(el) => (this._containerRef = el as HTMLElement)}
          onTransitionEnd={(event) => this.handleTransitionEnd(event)}
          tabIndex={-1}
          style={this.getSizeStyles()}
          class={{
            [`drawer-container anchor-${this.anchor}`]: true,
            [`size-${this.size}`]: isFluidSize,
            'drawer-visible': this.visible,
            'transition-state': this._transitioning,
          }}>
          <div class='drawer-header'>
            <CloseButton
              render={!this.hideCloseIcon}
              class='drawer-dismiss'
              title={
                this._i18nStore?.translate(
                  this.fluidTranslate?.dismissButton?.title
                ) || 'Close Drawer'
              }
              size={FluidSize.MEDIUM}
              scale={iconScale}
              theme={this.theme}
              onClick={() => this.toggleDrawer()}
            />
          </div>
          <div
            class={{
              'drawer-content': true,
              [`padding-${this.padding}`]: true,
            }}>
            <slot />
          </div>
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for Drawer container.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;
}
